import type { PropsWithChildren } from 'react';
import React from 'react';

import { ThemeProvider } from '@change/tomato-core-ui';

import { Box } from '@change-corgi/design-system/layout';

import type { DefaultLayoutProps } from '../shared/types';

import { Footer } from './components/Footer';
import { Header } from './components/Header';

export const LayoutV2 = ({
	hideHeader,
	hideFooter,
	hideBrowseLink,
	suppressHydrationWarning,
	children,
}: PropsWithChildren<DefaultLayoutProps> & { suppressHydrationWarning?: boolean }): React.JSX.Element | null => {
	if (hideFooter && hideHeader) {
		return (
			<Box
				// needed so that App can focus fallback on it
				data-maincontent-principal
				sx={{ height: '100%' }}
				role="main"
				suppressHydrationWarning={suppressHydrationWarning}
			>
				{children}
			</Box>
		);
	}

	return (
		<>
			{/* https://philipwalton.com/articles/normalizing-cross-browser-flexbox-bugs/ */}
			<Box suppressHydrationWarning={suppressHydrationWarning}>
				{!hideHeader && (
					<ThemeProvider>
						<Header hideBrowseLink={!!hideBrowseLink} className="shrink-0" />
					</ThemeProvider>
				)}
				<Box
					// needed so that App can focus fallback on it
					data-maincontent-principal
					// this is to avoid blank space after the footer
					// using flex:1 on the body currently doesn't work because of the banners being outside of the layout
					// and that can sometimes result in double scrollbars on desktop
					// 300px come from manually measuring the height of the footer and header
					// (not great but better than nothing for now)
					sx={{ minHeight: hideFooter ? undefined : [null, 'calc(100vh - 300px)'] }}
					data-qa="page-content"
					role="main"
				>
					{children}
				</Box>
				{!hideFooter && (
					<ThemeProvider>
						<Footer className="mb-8 shrink-0" />
					</ThemeProvider>
				)}
			</Box>
		</>
	);
};

export const HEADER_HEIGHT = 56;
